import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Upload,
    FileText,
    FileImage,
    Trash2,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    ShieldCheck,
    Eye,
    Download,
    Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,application/pdf,image/jpeg,image/jpg,image/png";
const MAX_MB = 15;

function relevanceBadge(rel) {
    if (rel === "red") return { cls: "bg-rose-100 text-rose-800", label: "Hohe Relevanz" };
    if (rel === "green") return { cls: "bg-emerald-100 text-emerald-800", label: "Geringe Relevanz" };
    return { cls: "bg-amber-100 text-amber-800", label: "Zu beachten" };
}

function DocTypeIcon({ ct }) {
    if (ct?.startsWith("image/")) return <FileImage className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
}

function DocCard({ doc, onDelete, onRelease }) {
    const [expanded, setExpanded] = useState(false);
    const isAnalyzing = doc.processing_status === "analyzing";
    const isError = doc.processing_status === "error";
    const rel = relevanceBadge(doc.relevance);

    const openFile = async () => {
        try {
            const res = await api.get(`/documents/${doc.document_id}/download`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error(formatApiError(e, "Download fehlgeschlagen"));
        }
    };

    return (
        <div className="rounded-2xl border border-ink-300/30 bg-white p-5" data-testid={`doc-card-${doc.document_id}`}>
            <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <DocTypeIcon ct={doc.content_type} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-base font-semibold text-ink-900" title={doc.original_filename}>
                        {doc.original_filename}
                    </div>
                    <div className="text-xs text-ink-500">
                        {(doc.size / 1024).toFixed(0)} KB · {new Date(doc.created_at).toLocaleString("de-DE")}
                    </div>
                </div>
                {isAnalyzing && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyse läuft
                    </span>
                )}
                {!isAnalyzing && !isError && doc.document_type && (
                    <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                        {doc.document_type}
                    </span>
                )}
                {!isAnalyzing && doc.relevance && (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${rel.cls}`}>{rel.label}</span>
                )}
                {isError && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-800">
                        <AlertTriangle className="h-3.5 w-3.5" /> Fehler
                    </span>
                )}
            </div>

            {!isAnalyzing && doc.analysis && (
                <div className="mt-4 rounded-xl bg-brand-50/40 p-4 text-sm text-ink-700">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-700">
                        <Sparkles className="h-3.5 w-3.5" /> KI-Zusammenfassung
                    </div>
                    <p className="mt-2">{doc.analysis.summary}</p>

                    {expanded && (
                        <div className="mt-4 space-y-3">
                            {Object.keys(doc.analysis.extracted_entities || {}).length > 0 && (
                                <div>
                                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-700">Extrahierte Daten</div>
                                    <dl className="grid gap-2 sm:grid-cols-2">
                                        {Object.entries(doc.analysis.extracted_entities).map(([k, v]) => (
                                            <div key={k} className="rounded-lg bg-white p-2.5">
                                                <dt className="text-[11px] uppercase tracking-wider text-ink-500">{k.replace(/_/g, " ")}</dt>
                                                <dd className="text-sm font-medium text-ink-900">{String(v)}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}
                            {doc.analysis.advisor_notes && (
                                <div>
                                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-700">Hinweise für die Beratung</div>
                                    <p className="rounded-lg bg-white p-3 text-sm">{doc.analysis.advisor_notes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        onClick={() => setExpanded((e) => !e)}
                        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-700"
                        data-testid={`doc-toggle-${doc.document_id}`}
                    >
                        {expanded ? "Weniger anzeigen" : "Details anzeigen"}
                    </button>
                </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm" onClick={openFile} className="rounded-full gap-2" data-testid={`doc-open-${doc.document_id}`}>
                    <Eye className="h-3.5 w-3.5" /> Ansehen
                </Button>
                <label className="ml-auto flex items-center gap-2 text-xs text-ink-700">
                    <Switch
                        checked={!!doc.released_to_advisor}
                        onCheckedChange={(v) => onRelease(doc, v)}
                        data-testid={`doc-release-${doc.document_id}`}
                    />
                    Für Berater freigeben
                </label>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(doc)}
                    className="rounded-full text-rose-600"
                    data-testid={`doc-delete-${doc.document_id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );
}

export default function DocumentsPage() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get("/documents");
            setDocs(data || []);
        } catch (e) {
            if (!silent) toast.error(formatApiError(e, "Dokumente konnten nicht geladen werden"));
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Poll silently while any is analyzing
    useEffect(() => {
        const analyzing = docs.some((d) => d.processing_status === "analyzing");
        if (!analyzing) return;
        const t = setInterval(() => load(true), 3000);
        return () => clearInterval(t);
    }, [docs, load]);

    const uploadFile = async (file) => {
        if (!file) return;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`Datei überschreitet ${MAX_MB} MB`);
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Dokument hochgeladen – Analyse läuft");
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Upload fehlgeschlagen"));
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) uploadFile(f);
    };

    const onDelete = async (doc) => {
        if (!window.confirm(`Dokument "${doc.original_filename}" wirklich löschen?`)) return;
        try {
            await api.delete(`/documents/${doc.document_id}`);
            toast.success("Dokument gelöscht");
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Löschen fehlgeschlagen"));
        }
    };

    const onRelease = async (doc, value) => {
        try {
            await api.patch(`/documents/${doc.document_id}/release`, { released_to_advisor: value });
            setDocs((ds) => ds.map((d) => (d.document_id === doc.document_id ? { ...d, released_to_advisor: value } : d)));
            toast.success(value ? "Für Berater freigegeben" : "Freigabe entzogen");
        } catch (e) {
            toast.error(formatApiError(e, "Speichern fehlgeschlagen"));
        }
    };

    return (
        <div className="min-h-[80vh] bg-brand-50/20 py-10">
            <div className="container max-w-4xl">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                            <ShieldCheck className="h-4 w-4" /> Sichere Dokumentenprüfung
                        </div>
                        <h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl">Ihre Unterlagen</h1>
                        <p className="mt-1 max-w-xl text-sm text-ink-500">
                            Heimverträge, Bescheide, Kostenblätter, Rentennachweise – sicher hochladen,
                            automatisch vorprüfen lassen, auf Wunsch dem Berater freigeben.
                        </p>
                    </div>
                    <Link to="/konto">
                        <Button variant="outline" className="rounded-full" data-testid="docs-back">Zum Konto</Button>
                    </Link>
                </div>

                {/* Uploader */}
                <div
                    className={`mt-8 rounded-3xl border-2 border-dashed ${
                        dragOver ? "border-brand-500 bg-brand-50/60" : "border-ink-300/40 bg-white"
                    } p-8 text-center transition`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    data-testid="docs-dropzone"
                >
                    <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                        <Upload className="h-6 w-6" />
                    </div>
                    <div className="mt-4 font-display text-lg font-semibold">Dokument hochladen</div>
                    <p className="mt-1 text-sm text-ink-500">
                        PDF, JPG oder PNG · max. {MAX_MB} MB · Ablegen oder Datei wählen
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => uploadFile(e.target.files?.[0])}
                        data-testid="docs-file-input"
                    />
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="mt-4 h-12 rounded-full bg-brand-500 hover:bg-brand-600 gap-2"
                        data-testid="docs-select-btn"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? "Wird hochgeladen…" : "Datei wählen"}
                    </Button>
                </div>

                {/* Info */}
                <div className="mt-6 rounded-2xl bg-brand-50/50 p-4 text-xs text-ink-700">
                    <div className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand-600" />
                        <div>
                            <strong>DSGVO-konform.</strong> Ihre Dokumente werden verschlüsselt übertragen und
                            gespeichert. Eine Weitergabe an den Berater erfolgt ausschließlich nach Ihrer
                            expliziten Freigabe je Dokument.
                        </div>
                    </div>
                </div>

                {/* List */}
                <div className="mt-8">
                    <h2 className="font-display text-xl font-bold">Ihre Dokumente</h2>
                    {loading ? (
                        <div className="mt-4 rounded-2xl bg-white p-8 text-center text-sm text-ink-500">Lade…</div>
                    ) : docs.length === 0 ? (
                        <div className="mt-4 rounded-2xl bg-white p-8 text-center text-sm text-ink-500">
                            Noch keine Dokumente hochgeladen.
                        </div>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {docs.map((d) => (
                                <DocCard key={d.document_id} doc={d} onDelete={onDelete} onRelease={onRelease} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
